import React from "react";
import {
  Activity,
  TrendingUp,
  Zap,
  ShieldCheck,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import { TIME_SERIES, SOURCE_BREAKDOWN, FUNNEL, EVENTS, formatEventType } from "@/data/mockData";
import { Card } from "@/components/ui/card";

export default function Dashboard() {
  const recentEvents = EVENTS.slice(0, 6);

  return (
    <div className="p-6 lg:p-10 space-y-8">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-caps mb-2">Overview · Last 14 days</div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-2 text-sm max-w-lg">
            Real-time telemetry across events, workflows, and detections.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground border border-border rounded-full px-3 py-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 pulse-dot" />
          Live · updated 4s ago
        </div>
      </div>

      {/* KPI cards -- all business focused */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        <Kpi label="Revenue driven by signals" value="$2.14M" delta="+18.6%" trend="up" icon={TrendingUp} sub="attributed to workflows · MTD" testid="kpi-revenue" />
        <Kpi label="Cost prevented" value="$418K" delta="+12.3%" trend="up" icon={ShieldCheck} sub="fraud + churn saved last 14d" testid="kpi-fraud" />
        <Kpi label="Automation ROI" value="8.4×" delta="+0.9" trend="up" icon={Zap} sub="vs. manual ops baseline" testid="kpi-roi" />
        <Kpi label="Card approval rate" value="72.4%" delta="+3.1pp" trend="up" icon={Activity} sub="platinum segment · MTD" testid="kpi-approval" />
        <Kpi label="Operator hours saved" value="1,240h" delta="+22%" trend="up" icon={Activity} sub="workflows replaced manual ops" testid="kpi-hours" />
        <Kpi label="Signal-to-action latency" value="38ms" delta="-4ms" trend="up" icon={Activity} sub="p50 across all workflows" testid="kpi-latency" />
      </div>

      {/* Volume chart + Source breakdown */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <Card className="xl:col-span-2 p-6 border-border">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="label-caps mb-1.5">Event volume</div>
              <div className="font-display text-2xl font-semibold">Ingestion timeline</div>
            </div>
            <div className="flex gap-2 text-xs">
              <LegendDot color="#124273" label="Events" />
              <LegendDot color="#E9B44C" label="Approved" />
              <LegendDot color="#9AA3A8" label="Declined" />
            </div>
          </div>
          <div className="h-72" data-testid="chart-volume">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={TIME_SERIES}>
                <defs>
                  <linearGradient id="gEvents" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#124273" stopOpacity={0.32} />
                    <stop offset="100%" stopColor="#124273" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 6,
                    fontSize: 12,
                  }}
                />
                <Area type="monotone" dataKey="events" stroke="#124273" strokeWidth={2} fill="url(#gEvents)" />
                <Area type="monotone" dataKey="approved" stroke="#E9B44C" strokeWidth={1.75} fill="transparent" />
                <Area type="monotone" dataKey="declined" stroke="#9AA3A8" strokeWidth={1.5} fill="transparent" strokeDasharray="4 3" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border-border">
          <div className="label-caps mb-1.5">Source mix</div>
          <div className="font-display text-2xl font-semibold mb-4">Where events come from</div>
          <div className="h-52" data-testid="chart-source">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={SOURCE_BREAKDOWN}
                  dataKey="value"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  stroke="hsl(var(--card))"
                >
                  {SOURCE_BREAKDOWN.map((s, i) => (
                    <Cell key={i} fill={s.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 6,
                    fontSize: 12,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1.5 mt-2">
            {SOURCE_BREAKDOWN.map((s) => (
              <div key={s.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-sm" style={{ background: s.color }} />
                  <span>{s.name}</span>
                </div>
                <span className="font-mono text-muted-foreground">{s.value}%</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Funnel + Recent events */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <Card className="xl:col-span-2 p-6 border-border">
          <div className="label-caps mb-1.5">Conversion</div>
          <div className="font-display text-2xl font-semibold mb-6">Credit card journey funnel</div>
          <div className="h-64" data-testid="chart-funnel">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={FUNNEL} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="stage" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} width={140} />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 6,
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="value" fill="#124273" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border-border" data-testid="recent-events-card">
          <div className="label-caps mb-1.5">Live feed</div>
          <div className="font-display text-2xl font-semibold mb-4">Recent events</div>
          <div className="space-y-3">
            {recentEvents.map((e) => (
              <div key={e.event_id} className="flex items-start gap-3 pb-3 border-b border-border last:border-0 last:pb-0">
                <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{formatEventType(e.event_type)}</div>
                  <div className="text-xs text-muted-foreground font-mono truncate">
                    {e.customer_code} · {new Date(e.event_datetime).toLocaleTimeString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

const Kpi = ({ label, value, delta, trend, icon: Icon, sub, testid }) => (
  <Card data-testid={testid} className="p-5 border-border relative group hover:border-primary/50 transition-colors">
    <div className="flex items-start justify-between mb-6">
      <div className="label-caps">{label}</div>
      <div className="w-9 h-9 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
        <Icon className="w-4 h-4" strokeWidth={1.5} />
      </div>
    </div>
    <div className="number-display text-4xl font-semibold">{value}</div>
    <div className="flex items-center gap-2 mt-3 text-xs">
      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm font-mono ${
          trend === "up" ? "text-emerald-500 bg-emerald-500/10" : "text-orange-500 bg-orange-500/10"
        }`}
      >
        {trend === "up" ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
        {delta}
      </span>
      <span className="text-muted-foreground">{sub}</span>
    </div>
  </Card>
);

const LegendDot = ({ color, label }) => (
  <div className="flex items-center gap-1.5 text-muted-foreground">
    <span className="w-2 h-2 rounded-sm" style={{ background: color }} />
    <span>{label}</span>
  </div>
);
